/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印埋点会话上下文
 */

import type { BatchEditWatermarkFromPlace } from '../analytics'

const SESSION_STORAGE_KEY = 'batchEditWatermarkAnalyticsContext'
const EXPOSURE_CONTEXT_MAX_AGE_MS = 30 * 60 * 1000
const ACTIVE_CONTEXT_IDLE_MAX_AGE_MS = 4 * 60 * 60 * 1000
const BATCH_EDIT_WATERMARK_FROM_PLACES: ReadonlySet<string> = new Set([
  'all_photo',
  'folder',
  'ai_folder',
  'add_photos',
  'direct'
])

interface BatchEditWatermarkAnalyticsContext {
  sessionID: string
  fromPlace: BatchEditWatermarkFromPlace
  createdAt: number
  lastActiveAt?: number
  photoNum: number
}

interface PreparedExposureAnalyticsContext {
  context: BatchEditWatermarkAnalyticsContext
  shouldTrack: boolean
}

const pendingExposureContexts = new Map<
  string,
  BatchEditWatermarkAnalyticsContext
>()

const getPendingExposureKey = (
  fromPlace: BatchEditWatermarkFromPlace,
  viewKey: string
): string => `${fromPlace}:${viewKey}`

const createSessionID = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `batch_edit_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

const isAnalyticsContext = (
  context: unknown
): context is BatchEditWatermarkAnalyticsContext => {
  if (!context || typeof context !== 'object') {
    return false
  }

  const sessionID = Reflect.get(context, 'sessionID')
  const fromPlace = Reflect.get(context, 'fromPlace')
  const createdAt = Reflect.get(context, 'createdAt')
  const lastActiveAt = Reflect.get(context, 'lastActiveAt')
  const photoNum = Reflect.get(context, 'photoNum')
  const activeAt =
    typeof lastActiveAt === 'number' && Number.isFinite(lastActiveAt)
      ? lastActiveAt
      : createdAt

  return (
    typeof sessionID === 'string' &&
    typeof fromPlace === 'string' &&
    BATCH_EDIT_WATERMARK_FROM_PLACES.has(fromPlace) &&
    typeof createdAt === 'number' &&
    Number.isFinite(createdAt) &&
    typeof activeAt === 'number' &&
    Number.isFinite(activeAt) &&
    Date.now() - activeAt <= ACTIVE_CONTEXT_IDLE_MAX_AGE_MS &&
    typeof photoNum === 'number' &&
    Number.isFinite(photoNum)
  )
}

const readAnalyticsContext = (): BatchEditWatermarkAnalyticsContext | null => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const rawContext = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!rawContext) {
      return null
    }
    const context: unknown = JSON.parse(rawContext)
    return isAnalyticsContext(context) ? context : null
  } catch {
    return null
  }
}

const persistAnalyticsContext = (
  context: BatchEditWatermarkAnalyticsContext
): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(context))
  } catch {
    // 埋点上下文持久化失败不能阻塞主流程。
  }
}

const createAnalyticsContext = (
  fromPlace: BatchEditWatermarkFromPlace,
  photoNum: number
): BatchEditWatermarkAnalyticsContext => {
  const now = Date.now()
  return {
    sessionID: createSessionID(),
    fromPlace,
    createdAt: now,
    lastActiveAt: now,
    photoNum
  }
}

const ensureAnalyticsContext = (
  fromPlace: BatchEditWatermarkFromPlace = 'direct',
  photoNum = 0
): BatchEditWatermarkAnalyticsContext => {
  const currentContext = readAnalyticsContext()
  if (currentContext?.fromPlace === fromPlace) {
    const nextContext = {
      ...currentContext,
      lastActiveAt: Date.now(),
      photoNum: photoNum || currentContext.photoNum
    }
    persistAnalyticsContext(nextContext)
    return nextContext
  }

  const nextContext = createAnalyticsContext(fromPlace, photoNum)
  persistAnalyticsContext(nextContext)
  return nextContext
}

/**
 * 生成曝光上下文但不持久化，避免仅曝光入口时覆盖当前编辑链路归因。
 *
 * @param fromPlace - 曝光入口来源
 * @param photoNum - 当前选中照片数量
 * @param viewKey - 当前路由视图唯一标识，同一视图重挂载时保持不变
 * @returns 尚未写入 sessionStorage 的曝光上下文及是否需要上报
 *
 * @example
 * ```ts
 * prepareExposureAnalyticsContext('all_photo', 3, 'route-view-key')
 * ```
 */
export const prepareExposureAnalyticsContext = (
  fromPlace: BatchEditWatermarkFromPlace,
  photoNum: number,
  viewKey: string
): PreparedExposureAnalyticsContext => {
  const pendingKey = getPendingExposureKey(fromPlace, viewKey)
  const pendingContext = pendingExposureContexts.get(pendingKey)
  if (
    pendingContext &&
    Date.now() - pendingContext.createdAt <= EXPOSURE_CONTEXT_MAX_AGE_MS
  ) {
    const updatedContext = {
      ...pendingContext,
      photoNum: photoNum || pendingContext.photoNum
    }
    pendingExposureContexts.set(pendingKey, updatedContext)
    return {
      context: updatedContext,
      shouldTrack: false
    }
  }

  const exposureContext = createAnalyticsContext(fromPlace, photoNum)
  pendingExposureContexts.set(pendingKey, exposureContext)
  return {
    context: exposureContext,
    shouldTrack: true
  }
}

/**
 * 在用户点击入口时持久化对应曝光上下文。
 *
 * @param fromPlace - 点击入口来源
 * @param photoNum - 点击时选中的照片数量
 * @param viewKey - 当前路由视图唯一标识
 * @returns 已激活的链路上下文
 *
 * @example
 * ```ts
 * activateEntryAnalyticsContext('folder', 2, 'route-view-key')
 * ```
 */
export const activateEntryAnalyticsContext = (
  fromPlace: BatchEditWatermarkFromPlace,
  photoNum = 0,
  viewKey = 'default'
): BatchEditWatermarkAnalyticsContext => {
  const pendingKey = getPendingExposureKey(fromPlace, viewKey)
  const pendingContext = pendingExposureContexts.get(pendingKey)
  pendingExposureContexts.delete(pendingKey)
  if (
    !pendingContext ||
    Date.now() - pendingContext.createdAt > EXPOSURE_CONTEXT_MAX_AGE_MS
  ) {
    const activeContext = createAnalyticsContext(fromPlace, photoNum)
    persistAnalyticsContext(activeContext)
    return activeContext
  }

  const activeContext = {
    ...pendingContext,
    lastActiveAt: Date.now(),
    photoNum: photoNum || pendingContext.photoNum
  }
  persistAnalyticsContext(activeContext)
  return activeContext
}

/**
 * 判断当前页面是否已持有有效的编辑链路上下文。
 *
 * @returns 有效上下文存在时返回 true
 */
export const hasCurrentAnalyticsContext = (): boolean =>
  readAnalyticsContext() !== null

/**
 * 结束当前编辑链路并清理暂存上下文。
 *
 * 离开批量编辑流程后调用，避免下一次直达编辑器复用旧入口归因。
 */
export const clearCurrentAnalyticsContext = (): void => {
  pendingExposureContexts.clear()
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // 埋点上下文清理失败不能阻塞页面离开。
  }
}

/**
 * 获取编辑页当前链路上下文，没有入口上下文时按 direct 创建。
 *
 * @param photoNum - 最新照片数量
 * @returns 当前有效链路上下文
 *
 * @example
 * ```ts
 * getCurrentAnalyticsContext(10)
 * ```
 */
export const getCurrentAnalyticsContext = (
  photoNum = 0
): BatchEditWatermarkAnalyticsContext => {
  const currentContext = readAnalyticsContext()
  return currentContext
    ? ensureAnalyticsContext(currentContext.fromPlace, photoNum)
    : ensureAnalyticsContext('direct', photoNum)
}
